import { isAvailable, isLocked } from "app/common/services/pageClaims";
import { swSettings } from "app/common/services/swSettings";
import { FiltersEnum } from "app/components/filters-bar/utils";
import { AccessWithoutMandatoryParamsHOC } from "app/components/React/HOCs/accessWithoutMandatoryParamsHOC";
import { withAllContextHoc } from "app/components/React/HOCs/withAllContextsHOC";
import { DisplayAdvertisingOverviewPage } from "app/pages/digital-marketing/ad-intelligence/advertisers/overview/DisplayAdvertisingOverviewPage";
import { PublisherAnalysisOverviewPage } from "app/pages/digital-marketing/ad-intelligence/publishers/overview/PublisherAnalysisOverviewPage";
import { PublisherAnalysisQueryBarHeader } from "app/pages/digital-marketing/ad-intelligence/publishers/overview/PublisherAnalysisQueryBarHeader";
import { SocialOverviewPage } from "app/pages/digital-marketing/social/SocialOverviewPage";
import { QualificationSideBarContainer } from "app/pages/website-analysis/components/qualification-sidebar/QualificationSideBarContainer";
import { WebsiteAnalysisModuleFrame } from "app/pages/website-analysis/frame/WebsiteAnalysisModuleFrame";
import { DEFAULT_CURRENCY } from "app/common/constants/currencies";
import SearchOverview from "app/pages/website-analysis/traffic-sources/search/overview/SearchOverview";
import {
    ECategoryReferralsType,
    ReferralsWrapper,
} from "app/pages/website-analysis/website-structure/pages/ReferralsWrapper";
import { filterConsistencyParams } from "app/routes/handlers/filter-consistency/filterConsistencyUtils";
import {
    onRedirectWebsiteAnalysis,
    onRedirectWebsiteAnalysisWithTotalWebSource,
} from "app/routes/handlers/filter-consistency/pipes/website-analysis/onRedirectWebsiteAnalysis";
import { fallback } from "app/routes/handlers/redirectFunctions/fallback";
import { redirectPipe } from "app/routes/handlers/redirectFunctions/redirectPipe";
import { default as DurationService, BasicDurations } from "app/services/DurationService";
import { devicesTypes } from "app/UtilitiesAndConstants/Constants/DevicesTypes";
import { resolvers } from "single-spa/react-app/config/resolvers/resolvers";
import type { RouterStateConfig } from "single-spa/react-app/router/IRouterState";

const trafficOverviewSearchParams: Record<string, unknown> = {
    Keywords_filters: null,
    groupedKeywords_filters: null,
    groupedKeywords_orderby: null,
    Ads_filters: null,
    IncludeOrganic: null,
    IncludePaid: null,
    IncludeBranded: null,
    IncludeNoneBranded: null,
    IncludeNewKeywords: null,
    IncludeTrendingKeywords: null,
    IncludeQuestions: null,
    limits: null,
    IncludeKeywordGroups: null,
    BooleanSearchTerms: null,
    search: null,
    selectedDomain: null,
    orderBy: null,
    ExcludeTerms: null,
    ExcludeUrls: null,
    IncludeUrls: null,
    IncludeTerms: null,
    family: null,
    source: null,
    selectedPhrase: null,
    volumeFromValue: null,
    volumeToValue: null,
    cpcFromValue: null,
    cpcToValue: null,
    difficultyFromValue: null,
    difficultyToValue: null,
    serp: null,
    ranking: null,
    positionTrends: null,
    intentsfilter: null,
    metric: null,
    ExcludeKeywordGroups: null,
};

const socialGroupConfig = {
    digitalsuite_website_social_overview: {
        parent: "digitalsuite_website",
        url: "/social/overview/:isWWW/:country/:duration?social_filters?webSource?sort?asc?search?source&key",
        defaultQueryParams: {
            webSource: devicesTypes.TOTAL,
            duration: "3m",
            isWWW: "*",
            country: 999,
        },
        params: {
            isWWW: "*",
            country: "999",
            duration: "3m",
            ...filterConsistencyParams,
        },
        component: AccessWithoutMandatoryParamsHOC(SocialOverviewPage, "key"),
        stateNameForEductionBar: "competitiveanalysis_website_social_overview",
        resolve: [resolvers.siteInfo],
        redirectTo: onRedirectWebsiteAnalysis(),
        pageId: {
            section: "website",
            subSection: "traffic",
            subSubSection: "social",
        },
        trackingId: {
            section: "Competitive Research",
            subSection: "Website Analysis/Social",
            subSubSection: "socialOverview",
        },
        icon: "sw-icon-social",
        pageTitle: "digitalsuite.analysis.social.overview.title",
        pageSubtitle: "analysis.social.overview.title.info",
        isUSStatesSupported: true,
        reloadOnSearch: true,
        homeState: "digitalsuite_websiteanalysis_home",
        data: {
            filtersConfig: {
                webSource: FiltersEnum.DISABLED,
            },
        },
    },
};

const displayGroupConfig = {
    digitalsuite_website_display_overview: {
        parent: "digitalsuite_website",
        url: "/display-overview/:isWWW/:country/:duration?key&webSource&adsViewType&adsSelectedSite&campaignsSelectedSite",
        component: AccessWithoutMandatoryParamsHOC(
            (props) => (
                <DisplayAdvertisingOverviewPage
                    {...props}
                    isPreview={true}
                />
            ),
            "key",
            "website",
        ),
        defaultQueryParams: {
            webSource: devicesTypes.TOTAL,
            duration: BasicDurations.LAST_TWENTY_EIGHT_DAYS,
            isWWW: "*",
            country: 826,
        },
        redirectTo: onRedirectWebsiteAnalysisWithTotalWebSource(),
        params: {
            isWWW: "*",
            country: "826",
            duration: BasicDurations.LAST_TWENTY_EIGHT_DAYS,
            ...filterConsistencyParams,
        },
        configId: "AdIntelligenceWAOverview",
        resolve: [resolvers.siteInfo],
        pageId: {
            section: "website",
            subSection: "traffic",
            subSubSection: "display",
        },
        trackingId: {
            section: "Competitive Research",
            subSection: "Website Analysis/Display",
            subSubSection: "displayOverview",
        },
        icon: "sw-icon-display-ads",
        pageTitle: "digitalsuite.analysis.display.overview.page.title",
        searchParams: ["webSource"],
        isUSStatesSupported: true,
        hidePageTitle: true,
        dynamic: true,
        resetOnStateChange: true,
        data: {
            trackPageViewOnSearchUpdate: false,
            filtersConfig: {
                webSource: FiltersEnum.DISABLED,
            },
        },
        homeState: "digitalsuite_websiteanalysis_home",
    },
};

const publisherGroupConfig = {
    digitalsuite_website_publisher_overview_frame: {
        parent: "digitalsuite_websiteanalysis-root",
        params: {
            key: {
                dynamic: false,
            },
        },
        url: "/digitalsuite/websiteanalysis-publisher",
        component: withAllContextHoc((props: FixmeType) => (
            <WebsiteAnalysisModuleFrame
                {...props}
                queryBarSearchHeader={<PublisherAnalysisQueryBarHeader />}
            />
        )),
        abstract: true,
        configId: "WebAnalysis",
        resolve: [resolvers.customCategories],
        data: {
            menuId: "websites",
            filtersConfig: {
                duration: FiltersEnum.ENABLED,
                country: FiltersEnum.ENABLED,
                webSource: FiltersEnum.ENABLED,
            },
        },
    },
    digitalsuite_website_publisher_overview: {
        parent: "digitalsuite_website_publisher_overview_frame",
        url: "/publisher-overview/:isWWW/:country/:duration?key&webSource&adsViewType",
        component: AccessWithoutMandatoryParamsHOC(
            (props) => (
                <PublisherAnalysisOverviewPage
                    {...props}
                    isPreview={true}
                />
            ),
            "key",
            "website",
            {
                title: "publisher.analysis.preview.overview.empty.state.title",
                subtitle: "publisher.analysis.preview.overview.empty.state.subtitle",
            },
        ),
        defaultQueryParams: {
            webSource: devicesTypes.TOTAL,
            duration: BasicDurations.LAST_TWENTY_EIGHT_DAYS,
            isWWW: "*",
            country: 826,
        },
        params: {
            isWWW: "*",
            country: "826",
            duration: BasicDurations.LAST_TWENTY_EIGHT_DAYS,
            ...filterConsistencyParams,
        },
        configId: "AdIntelligenceWAOverview",
        resolve: [resolvers.siteInfo],
        pageId: {
            section: "website",
            subSection: "traffic",
            subSubSection: "publisher",
        },
        trackingId: {
            section: "Competitive Research",
            subSection: "Website Analysis/Display",
            subSubSection: "publisherOverview",
        },
        icon: "sw-icon-publisher",
        pageTitle: "digitalsuite.analysis.publisher.overview.page.title",
        searchParams: ["webSource"],
        isUSStatesSupported: true,
        hidePageTitle: true,
        dynamic: true,
        resetOnStateChange: true,
        redirectTo: onRedirectWebsiteAnalysisWithTotalWebSource(),
        data: {
            trackPageViewOnSearchUpdate: false,
            filtersConfig: {
                webSource: FiltersEnum.DISABLED,
            },
        },
        homeState: "digitalsuite_websiteanalysis_home",
    },
};

const referralGroupConfig: RouterStateConfig = {
    digitalsuite_website_referrals: {
        parent: "digitalsuite_website",
        url: "/referrals/:isWWW/:country/:duration?webSource&referralsCategory&selectedTab&limits&orderBy&engagementTypeFilter&IncludeNewReferrals&IncludeTrendingReferrals&ExcludeUrls&IncludeUrls&outagoing_filters&selectedSite&outagoing_orderby&key",
        defaultQueryParams: { isWWW: "*", country: "999", duration: "3m", selectedTab: "incomingTraffic" },
        configId: "WebReferralTraffic",
        // @ts-ignore FIXME: strict migration - Type '... | null' is not assignable to type 'RedirectToResult' — transitionHookFn type excludes null but redirectPipe runtime explicitly handles null as "no redirect"
        redirectTo: (transition) => {
            if (isLocked(swSettings.components.WebReferralTraffic)) {
                return null;
            }

            const params = transition.params();
            const toState = transition.to();

            // Ensure all required path parameters are set - use defaults if missing
            // @ts-expect-error FIXME: Property 'defaultQueryParams' does not exist on type 'StateDeclaration'
            const isWWW = params.isWWW ?? toState?.defaultQueryParams?.isWWW ?? "*";
            let country = params.country;
            if (!country || country === null || country === undefined) {
                // @ts-expect-error FIXME: Property 'defaultQueryParams' does not exist on type 'StateDeclaration'
                country = toState?.defaultQueryParams?.country || "999";
            }
            // @ts-expect-error FIXME: Property 'defaultQueryParams' does not exist on type 'StateDeclaration'
            const duration = params.duration ?? toState?.defaultQueryParams?.duration ?? "3m";

            const updatedParams = {
                ...params,
                isWWW,
                country,
                duration,
            };

            const requestedTab = (params.selectedTab || "").toLowerCase();

            const candidates = [
                {
                    component: swSettings.components.WebReferralIncomingTraffic,
                    state: "digitalsuite_website_referrals_incomingtraffic",
                    selectedTab: ECategoryReferralsType.IncomingTraffic,
                },
                {
                    component: swSettings.components.WebReferralOutgoingTraffic,
                    state: "digitalsuite_website_referrals_outgoingtraffic",
                    selectedTab: ECategoryReferralsType.OutgoingTraffic,
                },
            ];
            const matchedByTab = candidates.find(
                ({ selectedTab, component }) => selectedTab.toLowerCase() === requestedTab && isAvailable(component),
            );

            if (matchedByTab) {
                return {
                    state: matchedByTab.state,
                    params: {
                        ...updatedParams,
                        selectedTab: matchedByTab.selectedTab,
                    },
                };
            }
            const fallback = candidates.find(({ component }) => isAvailable(component));

            if (fallback) {
                return {
                    state: fallback.state,
                    params: {
                        ...updatedParams,
                        selectedTab: fallback.selectedTab,
                    },
                };
            }

            // If no candidates are available, allow navigation to proceed to the audience state
            // This will show the wrapper component which handles the case when no tabs are available
            return {
                params: updatedParams,
            };
        },
        component: AccessWithoutMandatoryParamsHOC(ReferralsWrapper, "key"),
    },
    // V
    digitalsuite_website_referrals_incomingtraffic: {
        parent: "digitalsuite_website_referrals",
        configIdDeps: ["MobileWebReferrals"],
        defaultQueryParams: {
            webSource: devicesTypes.TOTAL,
            duration: "3m",
            isWWW: "*",
            country: 999,
        },
        params: {
            ...filterConsistencyParams,
        },
        redirectTo: redirectPipe(
            fallback({
                salesIntelligence: "accountreview_website_referrals_incomingtraffic",
            }),
            onRedirectWebsiteAnalysis(),
        ),
        qualificationSideBarComponent: QualificationSideBarContainer,
        resolve: [resolvers.siteInfo],
        stateNameForEductionBar: "competitiveanalysis_website_referrals_incomingtraffic",
        pageId: {
            section: "website",
            subSection: "traffic",
            subSubSection: "referrals",
        },
        trackingId: {
            section: "Competitive Research",
            subSection: "Website Analysis/Referral",
            subSubSection: "incomingTraffic",
        },
        icon: "sw-icon-referrals",
        childStates: {
            total: {
                configId: "TotalWebReferrals",
            },
        },
        hidePageTitle: true,
        pageTitle: "analysis.referrals.title",
        isUSStatesSupported: true,
        searchParams: ["webSource"],
        reloadOnSearch: true,
        data: {
            trackPageViewOnSearchUpdate: false,
        },
        reset: {
            ExcludeUrls: null,
            IncludeUrls: null,
        } as Record<string, unknown>,
    },
    digitalsuite_website_referrals_outgoingtraffic: {
        parent: "digitalsuite_website_referrals",
        configIdDeps: ["MobileWebReferrals"],
        defaultQueryParams: {
            webSource: devicesTypes.TOTAL,
            duration: "3m",
            isWWW: "*",
            country: 999,
        },
        params: {
            ...filterConsistencyParams,
        },
        redirectTo: redirectPipe(
            fallback({
                salesIntelligence: "accountreview_website_outgoingtraffic",
            }),
            onRedirectWebsiteAnalysis(),
        ),
        stateNameForEductionBar: "competitiveanalysis_website_referrals_outgoingtraffic",
        qualificationSideBarComponent: QualificationSideBarContainer,
        resolve: [resolvers.siteInfo],
        pageId: {
            section: "website",
            subSection: "traffic",
            subSubSection: "referrals",
        },
        trackingId: {
            section: "Competitive Research",
            subSection: "Website Analysis/Referral",
            subSubSection: "outgoingTraffic",
        },
        childStates: {
            total: {
                configId: "TotalWebReferrals",
            },
        },
        icon: "sw-icon-bounce-rate",
        hidePageTitle: true,
        pageTitle: "analysis.referrals.title",
        isUSStatesSupported: true,
        reloadOnSearch: true,
        homeState: "digitalsuite_websiteanalysis_home",
    },
};

const searchOverviewGroupConfig: RouterStateConfig = {
    digitalsuite_website_search_overview: {
        parent: "digitalsuite_website",
        url: `/search-overview/:isWWW/:country/:duration?selectedSingleOverviewGraphTab&selectedNonBrandedTab&webSource?key&performanceOverTimeChartCurrency?${Object.keys(
            trafficOverviewSearchParams,
        ).join(`?`)}`,
        defaultQueryParams: {
            duration: "3m",
            webSource: devicesTypes.TOTAL,
            isWWW: "*",
            performanceOverTimeChartCurrency: DEFAULT_CURRENCY,
        },
        params: {
            isWWW: "*",
            duration: "3m",
            performanceOverTimeChartCurrency: { type: "string", dynamic: true },
            ...filterConsistencyParams,
        },
        component: AccessWithoutMandatoryParamsHOC(SearchOverview, "key"),
        redirectTo: redirectPipe(
            fallback({
                salesIntelligence: "accountreview_website_search_overview",
            }),
            // @ts-ignore FIXME: strict migration - return type '... | null' not assignable to transitionHookFn — null is the redirectPipe convention for "no redirect"
            (transition) => {
                const params = transition.params();
                const duration = params.duration;
                if (duration) {
                    const durationData = DurationService.getDurationData(duration);
                    const monthsDiff = DurationService.getMonthsFromApiDuration(
                        durationData.raw.from,
                        durationData.raw.to,
                        false,
                    );
                    if (monthsDiff > 12) {
                        return {
                            state: "digitalsuite_website_search_overview",
                            params: { ...params, duration: "12m", isDurationChanged: true },
                        };
                    }
                }
                return null;
            },
        ),
        stateNameForEductionBar: "competitiveanalysis_website_search_overview",
        resolve: [resolvers.siteInfo],
        pageId: {
            section: "website",
            subSection: "traffic",
            subSubSection: "search-overview",
        },
        trackingId: {
            section: "Competitive Research",
            subSection: "Website Analysis/Search",
            subSubSection: "overview",
        },
        icon: "sw-icon-search",
        hidePageTitle: true,
        searchParams: ["webSource"],
        pageTitle: "competitors.search.overview.page.title",
        pageSubtitle: "competitors.search.overview.page.title.info",
        isUSStatesSupported: true,
        reloadOnSearch: true,
        configId: "WebAnalysisSearchOverview",
        configIdDeps: [
            "AudienceOverview",
            "KeywordAnalysis",
            "MobileWebSearch",
            "MobileMarketingMix",
            "MarketingMixTotal",
        ],
        data: {
            trackPageViewOnSearchUpdate: false,
        },
        homeState: "digitalsuite_websiteanalysis_home",
    },
};

export const competitionMarketingAnalysisConfig = {
    ...searchOverviewGroupConfig,
    ...referralGroupConfig,
    ...displayGroupConfig,
    ...publisherGroupConfig,
    ...socialGroupConfig,
};
